import React from "react";
import { Controller } from "react-hook-form";
import { InputLabel, AppInputWrapper, ErrorLabel } from "./AppInput.styles";

interface AppInputProps {
  label: string;
  name: string;
  control: any;
  errors: any;
  placeholder: string;
  type?: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  successStyle?: boolean;
  disabled?: boolean;
  overrideBorderBottomColor?: string;
  min?: string | number;
  max?: string | number;
}

const AppInput = React.forwardRef<HTMLInputElement, AppInputProps>(
  (
    {
      label,
      name,
      control,
      errors,
      placeholder,
      type = "text",
      icon,
      iconRight,
      successStyle,
      disabled,
      overrideBorderBottomColor,
      min,
      max,
    },
    ref
  ) => {
    return (
      <div style={{ marginBottom: "1rem", width: "100%", minHeight: 100 }}>
        <InputLabel disabled={disabled} htmlFor={name}>
          {label}
        </InputLabel>
        <AppInputWrapper
          overrideBorderBottomColor={overrideBorderBottomColor}
          disabled={disabled}
          $success={successStyle}
          $leftIcon={!!icon}
          $iconRight={!!iconRight}
        >
          {icon && <span className="input-icon">{icon}</span>}
          {iconRight && <span className="input-icon-right">{iconRight}</span>}
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                ref={ref}
                disabled={disabled}
                id={name}
                type={type}
                placeholder={placeholder}
                min={min}
                max={max}
                className={`app-input ${errors[name] ? "error" : ""}`}
              />
            )}
          />
        </AppInputWrapper>
        {errors[name] && (
          <ErrorLabel className="error-message">
            {errors[name]?.message}
          </ErrorLabel>
        )}
      </div>
    );
  }
);

AppInput.displayName = "AppInput"; // Set a display name for better debugging

export default AppInput;

interface UnControlledAppInputProps {
  label: string;
  name: string;
  errors?: any;
  placeholder: string;
  type?: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  successStyle?: boolean;
  disabled?: boolean;
  value: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  minHeight?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  blueStyle?: boolean;
}

export const UnControlledAppInput = React.forwardRef<
  HTMLInputElement,
  UnControlledAppInputProps
>(
  (
    {
      label,
      name,
      placeholder,
      type = "text",
      icon,
      iconRight,
      successStyle,
      disabled,
      value,
      autoFocus,
      onBlur,
      onChange,
      minHeight = 100,
      onKeyDown = (): void => {},
      onFocus = (): void => {},
      blueStyle,
    },
    ref
  ) => {
    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          marginBottom: "1rem",
          width: "100%",
          minHeight: minHeight,
          justifyContent: "center",
          display: "flex",
        }}
      >
        <InputLabel disabled={disabled} htmlFor={name}>
          {label}
        </InputLabel>
        <AppInputWrapper
          blueStyle={blueStyle}
          disabled={disabled}
          $success={successStyle}
          $leftIcon={!!icon}
          $iconRight={!!iconRight}
        >
          {icon && <span className="input-icon">{icon}</span>}
          {iconRight && <span className="input-icon-right">{iconRight}</span>}
          <input
            onClick={(e) => e.stopPropagation()}
            ref={ref}
            disabled={disabled}
            id={name}
            type={type}
            placeholder={placeholder}
            value={value}
            className={`app-input`}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
            onFocus={onFocus}
          />
        </AppInputWrapper>
      </div>
    );
  }
);

UnControlledAppInput.displayName = "UnControlledAppInput"; // Set a display name for better debugging
